import { useNavigate } from 'react-router-dom';
import { Stack, Group, Text, Button } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TenantName } from '../../../components/TenantName';
import { CopyableId } from '../../../components/CopyableId';
import { formatDateTime } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import { AUTO_DISABLE_AT, EventChips, FailureMeter, StateBadge, UrlCell, type Subscription } from './parts';
import '../webhooks.css';

/**
 * One subscription, read whole.
 *
 * The list answers "which endpoint is failing"; the next question is always
 * "what is it, and what did the last attempt return". Sending the operator to
 * the tenant page answered neither, so the row now opens this instead and links
 * onward to the deliveries scoped to this endpoint.
 *
 * The panel is deliberately read-only. Disable, replay and delete all exist in
 * the webhooks service, but only on tenant-scoped routes that need a tenant
 * JWT, so offering the buttons here would mean offering buttons that cannot
 * work. The notice says so rather than leaving the absence unexplained.
 */
export function SubscriptionDrawer({ row, onClose }: { row: Subscription | null; onClose: () => void }) {
  const t = useT();
  const nav = useNavigate();

  return (
    <Drawer opened={row !== null} onClose={onClose} title={t('webhooks.detail.title')} position="right" size="md">
      {row && (
        <Stack gap="lg">
          <Group gap="sm" align="center" wrap="wrap">
            <TenantName id={row.organisation_id} withCopy />
            <StateBadge r={row} t={t} />
          </Group>

          <Stack gap="sm" className="rr-wh-detail__section">
            <Text role="overline" c="var(--rr-color-text-muted)">{t('webhooks.detail.endpoint')}</Text>
            <Stack gap="xs">
              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.detail.url')}</Text>
              <UrlCell url={row.url} />
            </Stack>
          </Stack>

          <Stack gap="sm" className="rr-wh-detail__section">
            <Text role="overline" c="var(--rr-color-text-muted)">
              {t('webhooks.detail.events', { count: (row.event_types ?? []).length })}
            </Text>
            <EventChips events={row.event_types ?? []} max={99} />
          </Stack>

          <Stack gap="sm" className="rr-wh-detail__section">
            <Text role="overline" c="var(--rr-color-text-muted)">{t('webhooks.detail.health')}</Text>
            <div className="rr-wh-detail__grid">
              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.detail.status')}</Text>
              <StateBadge r={row} t={t} />

              <Text role="caption" c="var(--rr-color-text-muted)">
                {t('webhooks.columns.failures', { threshold: AUTO_DISABLE_AT })}
              </Text>
              <FailureMeter r={row} t={t} />

              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.detail.created')}</Text>
              <Text role="bodySmall">{formatDateTime(row.created_at)}</Text>

              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.detail.lastDelivery')}</Text>
              {/* The admin projection carries no last-delivery timestamp, and a
                  dash here would read as "never delivered" rather than as
                  "not returned by this endpoint". */}
              <Text role="caption" c="var(--rr-color-text-muted)">{t('webhooks.detail.noLastDelivery')}</Text>
            </div>
          </Stack>

          <Stack gap="sm">
            <Group gap="sm" wrap="wrap">
              <Button size="sm" onClick={() => nav(`/webhooks/deliveries?subscription_id=${row.id}`)}>
                {t('webhooks.detail.viewDeliveries')}
              </Button>
              <Button size="sm" variant="default" onClick={() => nav(`/tenants/${row.organisation_id}`)}>
                {t('webhooks.viewTenant')}
              </Button>
              <CopyableId value={row.id} label={t('webhooks.deliveries.copySubscriptionId')} />
            </Group>
            <Alert color="info">{t('webhooks.detail.readOnly')}</Alert>
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}
